/**
 * EditMainInfoModal (S1.4) — chunked edit for 主要資料: display names, units,
 * dimensions/weight. SKU is NOT edited here (the wizard owns the dup-check
 * FSM); Internal Product ID is system identity and never enterable.
 */

import { useEffect, useMemo, useState } from 'react';
import { Col, Input, InputNumber, Modal, Row, Select, Space, Typography, theme } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { useProductMutations } from '@/hooks/useProducts';
import { PRODUCT_WEIGHT_UNITS } from '@/types/api/product';
import type { Product, ProductWeightUnit } from '@/types/api/product';
import { UNIT_VALUES } from '@/schemas/forms/productFormSchemas';
import { editMainInfoSchema } from '../schemas';
import type { EditMainInfoFormData } from '../schemas';

const { Text } = Typography;

// 單位 family: ONE home — `UNIT_VALUES` in @/schemas/forms/productFormSchemas
// (W7-B3). Re-declaring it here is what let the wizard and this modal drift;
// unitValuesSingleHome.test.ts is the source-scan that keeps it at one.

interface EditMainInfoModalProps {
  open: boolean;
  product: Product;
  onCancel: () => void;
  onSaved: () => void;
}

export const EditMainInfoModal = ({ open, product, onCancel, onSaved }: EditMainInfoModalProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { handleError, handleSuccess } = useErrorHandler();
  const { updateProduct } = useProductMutations(product.id);

  // Item Weight Unit (0708-J) — entry qualifier beside the weight input
  const [weightUnit, setWeightUnit] = useState<ProductWeightUnit>(product.weightUnit ?? 'kg');

  const formValues = useMemo<EditMainInfoFormData>(
    () => ({
      displayName: product.displayName,
      nameZh: product.nameZh ?? '',
      stockUnit: product.stockUnit,
      purchaseUnit: product.purchaseUnit,
      saleUnit: product.saleUnit,
      lengthMm: product.lengthMm ?? null,
      widthMm: product.widthMm ?? null,
      heightMm: product.heightMm ?? null,
      weightKg: product.weightKg ?? null,
    }),
    [product]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditMainInfoFormData>({
    resolver: zodResolver(editMainInfoSchema),
    defaultValues: formValues,
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      reset(formValues);
      setWeightUnit(product.weightUnit ?? 'kg');
    }
  }, [open, formValues, reset, product.weightUnit]);

  const onSubmit = async (data: EditMainInfoFormData) => {
    try {
      await updateProduct({
        displayName: data.displayName,
        nameZh: data.nameZh ?? '',
        stockUnit: data.stockUnit,
        purchaseUnit: data.purchaseUnit,
        saleUnit: data.saleUnit,
        lengthMm: data.lengthMm ?? undefined,
        widthMm: data.widthMm ?? undefined,
        heightMm: data.heightMm ?? undefined,
        weightKg: data.weightKg ?? undefined,
        weightUnit,
      });
      handleSuccess(t('products:messages.updated'));
      onSaved();
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  const requiredMark = <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>;

  const unitSelect = (name: 'stockUnit' | 'purchaseUnit' | 'saleUnit', labelKey: string) => (
    <Col span={8}>
      <Text style={formStyles.labelStyle}>
        {requiredMark}
        {t(labelKey)}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <>
            <Select
              {...field}
              size="large"
              options={UNIT_VALUES.map((unit) => ({ label: unit, value: unit }))}
              status={errors[name] && touchedFields[name] ? 'error' : undefined}
              style={formStyles.inputStyle}
              onChange={(value) => {
                field.onChange(value);
                trigger(name);
              }}
            />
            {errors[name] && touchedFields[name] && (
              <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
            )}
          </>
        )}
      />
    </Col>
  );

  const dimField = (name: 'lengthMm' | 'widthMm' | 'heightMm' | 'weightKg', labelKey: string) => (
    <Col span={6}>
      <Text style={formStyles.labelStyle}>{t(labelKey)}</Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <InputNumber
            value={field.value ?? null}
            onChange={(value) => field.onChange(value)}
            onBlur={field.onBlur}
            size="large"
            min={0}
            style={formStyles.inputStyle}
          />
        )}
      />
    </Col>
  );

  return (
    <Modal
      title={t('products:detail.modals.editMain')}
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit(onSubmit)}
      okText={t('common:actions.save')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isSubmitting}
      width={800}
      okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
    >
      <form>
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:sections.coreIdentity')}
          </Text>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>
                {requiredMark}
                {t('products:fields.displayName')}
              </Text>
              <Controller
                name="displayName"
                control={control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      size="large"
                      status={errors.displayName && touchedFields.displayName ? 'error' : undefined}
                      style={formStyles.inputStyle}
                    />
                    {errors.displayName && touchedFields.displayName && (
                      <Text style={formStyles.errorStyle}>
                        {t(errors.displayName.message ?? '')}
                      </Text>
                    )}
                  </>
                )}
              />
            </Col>
            <Col span={12}>
              <Text style={formStyles.labelStyle}>{t('products:fields.nameZh')}</Text>
              <Controller
                name="nameZh"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" style={formStyles.inputStyle} />
                )}
              />
            </Col>
          </Row>
        </div>

        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:sections.units')}
          </Text>
          <Row gutter={[16, 16]}>
            {unitSelect('stockUnit', 'products:fields.stockUnit')}
            {unitSelect('purchaseUnit', 'products:fields.purchaseUnit')}
            {unitSelect('saleUnit', 'products:fields.saleUnit')}
          </Row>
        </div>

        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:wizard.sections.physical')}
          </Text>
          <Row gutter={[16, 16]}>
            {dimField('lengthMm', 'products:fields.lengthMm')}
            {dimField('widthMm', 'products:fields.widthMm')}
            {dimField('heightMm', 'products:fields.heightMm')}
            <Col span={6}>
              {/* Weight value + Item Weight Unit qualifier (0708-J) */}
              <Text style={formStyles.labelStyle}>{t('products:fields.weight')}</Text>
              <Controller
                name="weightKg"
                control={control}
                render={({ field }) => (
                  <Space.Compact style={{ width: '100%' }}>
                    <InputNumber
                      value={field.value ?? null}
                      onChange={(value) => field.onChange(value)}
                      onBlur={field.onBlur}
                      size="large"
                      min={0}
                      style={{ flex: 1 }}
                    />
                    <Select
                      size="large"
                      aria-label={t('products:fields.weightUnit')}
                      value={weightUnit}
                      options={PRODUCT_WEIGHT_UNITS.map((unit) => ({
                        label: t(`products:weightUnits.${unit}`),
                        value: unit,
                      }))}
                      onChange={(value) => setWeightUnit(value)}
                      style={{ width: 72 }}
                    />
                  </Space.Compact>
                )}
              />
            </Col>
          </Row>
          <Text
            type="secondary"
            style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '8px' }}
          >
            {t('products:wizard.cbmAuto')}
          </Text>
        </div>
      </form>
    </Modal>
  );
};

export default EditMainInfoModal;
